// src/pages/MentalPlus/IslandTile.tsx
import * as React from "react";
import {
  motion,
  useDragControls,
  useMotionValue,
} from "framer-motion";
import styled from "styled-components";
import type { IslandDef, WeatherKey } from "./IslandRegistry";
import { useTodayScores } from "../../hooks/useScoredToday";
import { useIslandVisibility } from "../../components/organisms/ResonanceContext/IslandVisibilityContext";

type SVGComponent = React.ComponentType<any>;

type Props = {
  def: IslandDef;
  contentRef: React.RefObject<HTMLDivElement>;
  getPos: (key: string, dx?: number, dy?: number) => { x: number; y: number };
  setPosition: (key: string, x: number, y: number) => void;
  draggingRef: React.MutableRefObject<boolean>;
  deleteTarget: string | null;
  setDeleteTarget: (key: string | null) => void;
  startLongPress: (key: string) => void;
  cancelLongPress: () => void;
  longPressTriggeredRef: React.MutableRefObject<boolean>;
  squelchNextTapRef: React.MutableRefObject<boolean>;
  onOpen: (key: string) => void;
  onRename: (key: string) => void;
  setVisibility: (key: string, value: boolean) => void;
  Wrapper?: React.ComponentType<React.ComponentProps<typeof IslandWrapper>>;
  draggable?: boolean;
};

export const IslandTile: React.FC<Props> = ({
  def,
  contentRef,
  getPos,
  setPosition,
  draggingRef,
  deleteTarget,
  setDeleteTarget,
  startLongPress,
  cancelLongPress,
  longPressTriggeredRef,
  squelchNextTapRef,
  onOpen,
  onRename,
  setVisibility,
  Wrapper = IslandWrapper,
  draggable = true,
}) => {
  const { getTitle } = useIslandVisibility();
  const lastWasLongRef = React.useRef(false);

  const { key, NeutralComponent, dx = 0, dy = 0 } = def;
  const title = getTitle(key);

  const pos = getPos(key, dx, dy);
  const isDelete = deleteTarget === key;
  const iconWidth = def.iconWidth ?? "90px";
  const iconSize = iconWidth;

  const { hasScoreToday, todayScores } = useTodayScores();

  const iconSourceCfg = def.iconFromInline ? def.inline ?? def.main : def.main;
  const iconSequenceId = iconSourceCfg?.sequenceId;

  const hasScore = !!iconSequenceId && hasScoreToday.has(iconSequenceId);
  const todaysScore =
    iconSequenceId != null ? todayScores.get(iconSequenceId) : undefined;

  const iconScale =
    iconSourceCfg?.scale?.mode === "sprite" ? iconSourceCfg.scale : undefined;

  let Icon: SVGComponent = NeutralComponent;

  if (def.weatherSprites) {
    if (!hasScore) {
      Icon = def.weatherSprites.nuit ?? NeutralComponent;
    } else if (todaysScore != null && iconScale) {
      const spriteKey = iconScale.map(todaysScore) as WeatherKey;
      Icon = def.weatherSprites[spriteKey] ?? NeutralComponent;
    } else {
      Icon = def.weatherSprites.soleil ?? NeutralComponent;
    }
  }

  // ✅ Motion values so we can persist final clamped values reliably
  const x = useMotionValue(pos.x);
  const y = useMotionValue(pos.y);

  React.useEffect(() => {
    x.set(pos.x);
    y.set(pos.y);
  }, [pos.x, pos.y, x, y]);

  // ✅ Drag should only start after a threshold (prevents micro-move killing long-press)
  const dragControls = useDragControls();
  const startPtRef = React.useRef<{ x: number; y: number } | null>(null);
  const didStartDragRef = React.useRef(false);
  const DRAG_THRESHOLD_PX = 10;

  // -------------------------
  // Interaction logic
  // -------------------------
  const commonHitAreaHandlers = {
    onPointerDown: (e: React.PointerEvent) => {
      if (deleteTarget) {
        setDeleteTarget(null);
        squelchNextTapRef.current = true;
        return;
      }
      if (draggingRef.current) return;

      startPtRef.current = { x: e.clientX, y: e.clientY };
      didStartDragRef.current = false;

      startLongPress(key);
    },

    onPointerMove: (e: React.PointerEvent) => {
      if (!draggable) return;
      if (longPressTriggeredRef.current) return; // long press already won
      if (!startPtRef.current) return;
      if (didStartDragRef.current) return;

      const dxm = e.clientX - startPtRef.current.x;
      const dym = e.clientY - startPtRef.current.y;
      const dist = Math.hypot(dxm, dym);

      if (dist >= DRAG_THRESHOLD_PX) {
        didStartDragRef.current = true;
        draggingRef.current = true;
        cancelLongPress();
        dragControls.start(e as any);
      }
    },

    onPointerUp: () => {
      lastWasLongRef.current = longPressTriggeredRef.current;
      startPtRef.current = null;
      cancelLongPress();
    },

    onPointerCancel: () => {
      lastWasLongRef.current = false;
      startPtRef.current = null;
      cancelLongPress();
    },

    onPointerLeave: () => {
      lastWasLongRef.current = false;
      startPtRef.current = null;
      cancelLongPress();
    },

    onClick: () => {
      if (squelchNextTapRef.current) {
        squelchNextTapRef.current = false;
        lastWasLongRef.current = false;
        return;
      }

      if (lastWasLongRef.current || draggingRef.current) {
        lastWasLongRef.current = false;
        return;
      }

      onOpen(key);
    },
  };

  return (
    <motion.div
      key={key}
      className="no-context-menu island-tile" // ✅ keep island-tile for overflow measurement
      data-island-key={key}
      initial={false}
      style={{ x, y, touchAction: "none", willChange: "transform" }}
      onContextMenu={(e) => e.preventDefault()}
      drag={draggable}
      dragControls={dragControls}
      dragListener={false} // ✅ critical: no automatic drag on touch
      dragConstraints={contentRef}
      dragElastic={0}
      dragMomentum={false}
      whileDrag={{ scale: 1.03, zIndex: 2 }}
      onDragEnd={() => {
        // ✅ persist clamped final values
        setPosition(key, x.get(), y.get());
        squelchNextTapRef.current = true; // avoid “tap after drag”
        requestAnimationFrame(() => (draggingRef.current = false));
      }}
    >
      <Wrapper>
        <motion.div
          className="hit-area"
          style={{ display: "inline-block", position: "relative" }}
          animate={isDelete ? { rotate: [0, -3, 3, -3, 0] } : { rotate: 0 }}
          transition={
            isDelete
              ? { duration: 0.6, repeat: Infinity, ease: "easeInOut" }
              : { duration: 0.2 }
          }
          {...commonHitAreaHandlers}
        >
          <motion.div
            className="island-button"
            whileHover={{ y: -4 }}
            whileTap={{ y: 2, scale: 0.985 }}
            transition={{ type: "spring", stiffness: 400, damping: 28 }}
          >
            <Icon className="island-svg" style={{ width: iconSize, height: iconSize }} />

            {isDelete && (
              <>
                {/* ✎ Rename */}
                <EditButton
                  type="button"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRename(key);
                  }}
                  aria-label={`Rename ${title}`}
                  title={`Rename ${title}`}
                >
                  ✎
                </EditButton>

                {/* × Delete */}
                <DeleteButton
                  type="button"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    setVisibility(key, false);
                    setDeleteTarget(null);
                  }}
                  aria-label={`Delete ${title}`}
                  title={`Delete ${title}`}
                >
                  ×
                </DeleteButton>
              </>
            )}
          </motion.div>
        </motion.div>

        <IslandTitle>{title}</IslandTitle>
      </Wrapper>
    </motion.div>
  );
};

// ——— Styles ———
export const IslandWrapper = styled.div<{ dx?: number; dy?: number }>`
  position: relative;
  flex: 0 0 auto;
  display: inline-flex;
  max-width: 100px;
  user-select: none;

  @media (max-height: 1100px) { height: 110px; }
  @media (max-height: 1000px) { height: 105px; }
  @media (max-height: 900px)  { height: 100px; }
  @media (max-height: 800px)  { height: 95px;  }
  @media (max-height: 700px)  { height: 90px;  }

  @media (max-width: 420px) { height: 100px; }
  @media (max-width: 400px) { height: 95px;  }
  @media (max-width: 380px) { height: 90px;  }
  @media (max-width: 365px) { height: 86px;  }

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 0.5rem;

  /* ---------- 3D button container ---------- */
  .island-button {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 4px;                     /* visible “plate” around the SVG */
    border-radius: 100px;
    background: linear-gradient(180deg, var(--plate), var(--plate-2));
    border: 1px solid var(--edge);
    box-shadow:
      /* raised body */
      0 10px 18px -6px var(--shadow-2),
      0 4px 10px -2px var(--shadow-1),
      /* subtle inner edges */
      0 0 0 0.5px var(--edge) inset,
      0 1px 0 0 var(--plate) inset,
      0 -2px 6px 0 var(--edge-dark) inset;
    transform-style: preserve-3d;
    will-change: transform;
    touch-action: manipulation;
  }

  /* optional hover glow on the SVG */
  .island-button:hover .island-svg {
    filter: drop-shadow(0 2px 8px rgba(170, 250, 255, 0.7));
  }

  /* SVG itself */
  .island-svg {
    cursor: pointer;
    display: block;
    transition: filter 0.2s;
    flex-shrink: 0;
    pointer-events: auto;
  }

  .island-svg,
  .island-svg * {
    -webkit-touch-callout: none;
    -webkit-tap-highlight-color: transparent;
    -webkit-user-select: none;
    user-select: none;
    outline: none;
  }

  /* position delete button relative to the raised plate */
  .delete-button {
    position: absolute;
    top: -8px;
    right: -8px;
  }

  @media (prefers-reduced-motion: reduce) {
    .island-button { transition: none; }
  }
`;

export const CenterIslandWrapper = styled(IslandWrapper)`
  margin: 0 auto;
  height: 100%;

  /* ✅ Big teal glow only for the center island */
  .island-button {
    position: relative;

    /* keep your existing shadows and ADD glow on top */
    box-shadow:
      /* 🌊 teal glow (bigger) */
      0 0 30px rgba(148, 241, 239, 0.70),
      0 0 70px rgba(148, 241, 239, 0.50),
      0 0 140px rgba(148, 241, 239, 0.35),

      /* existing plate shadows */
      0 10px 18px -6px var(--shadow-2),
      0 4px 10px -2px var(--shadow-1),
      0 0 0 0.5px var(--edge) inset,
      0 1px 0 0 var(--plate) inset,
      0 -2px 6px 0 var(--edge-dark) inset;
  }

  /* ✅ Huge soft aura behind the button (no layout impact) */
  .island-button::before {
    content: "";
    position: absolute;
    inset: -35%;
    border-radius: 50%;
    background: radial-gradient(
      circle,
      rgba(148, 241, 239, 0.45) 0%,
      rgba(148, 241, 239, 0.25) 35%,
      rgba(148, 241, 239, 0.10) 55%,
      transparent 70%
    );
    filter: blur(18px);
    z-index: -1;
    pointer-events: none;
  }

  /* Optional: make the icon itself glow a bit too */
  .island-svg {
    filter:
      drop-shadow(0 0 22px rgba(148, 241, 239, 0.55))
      drop-shadow(0 0 55px rgba(148, 241, 239, 0.35));
  }
`;


export const IslandTitle = styled.div`
  text-align: center;
  width: 100%;
  color: #2a2a2a;
  font-weight: 500;
  letter-spacing: 0.01em;
  white-space: normal;
  max-width: 120px;
`;

export const DeleteButton = styled.button`
  position: absolute;
  top: -6px;
  right: -6px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: none;
  font-size: 16px;
  line-height: 1;
  background: rgba(0,0,0,0.72);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
  box-shadow: 0 2px 6px rgba(0,0,0,0.25);
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  touch-action: manipulation;
  cursor: pointer;
`;

export const EditButton = styled(DeleteButton)`
  left: -6px;     /* ✅ left top */
  right: auto;    /* ✅ cancel inherited right */
  font-size: 14px;
`;
